export interface PolicySection {
  id: string;
  title: string;
  content: string[];
  subsections?: {
    title: string;
    content: string[];
  }[];
}

export interface PolicyMetadata {
  effectiveDate: string;
  lastUpdated: string;
  version: string;
  jurisdiction: string;
  dataController: string;
}

export const policyMetadata: PolicyMetadata = {
  effectiveDate: "January 1, 2025",
  lastUpdated: "January 15, 2025",
  version: "4.1",
  jurisdiction: "State of Delaware, United States",
  dataController: "Unimerg Inc.",
};

export const policyTableOfContents = [
  { id: "introduction", title: "Introduction & Scope" },
  { id: "information-collected", title: "Information We Collect" },
  { id: "how-we-collect", title: "How We Collect Information" },
  { id: "use-of-information", title: "How We Use Your Information" },
  { id: "legal-basis", title: "Legal Basis for Processing" },
  { id: "sharing-information", title: "Sharing Your Information" },
  { id: "data-retention", title: "Data Retention" },
  { id: "data-security", title: "Data Security" },
  { id: "international-transfers", title: "International Data Transfers" },
  { id: "your-rights", title: "Your Privacy Rights" },
  { id: "cookies-tracking", title: "Cookies & Tracking Technologies" },
  { id: "children-privacy", title: "Children's Privacy" },
  { id: "third-party-links", title: "Third-Party Links & Services" },
  { id: "policy-changes", title: "Changes to This Policy" },
  { id: "contact-us", title: "Contact Us" },
  { id: "jurisdiction-specific", title: "Jurisdiction-Specific Disclosures" },
];

export const policySections: PolicySection[] = [
  {
    id: "introduction",
    title: "1. Introduction & Scope",
    content: [
      'Unimerg Inc. ("Unimerg," "Company," "we," "us," or "our") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy ("Policy") describes how we collect, use, disclose, store, and protect your personal information when you use our platform, website, applications, and services (collectively, the "Services").',
      "This Policy applies to all individuals who interact with our Services, including contributors, visitors, job applicants, and any other users. By accessing or using our Services, you acknowledge that you have read, understood, and agree to the practices described in this Policy.",
      "We encourage you to read this Policy carefully and contact us if you have any questions. Your privacy matters to us, and we are dedicated to being transparent about our data practices.",
    ],
    subsections: [
      {
        title: "1.1 Data Controller",
        content: [
          "Unimerg Inc. is the data controller responsible for your personal information. Our contact details are provided at the end of this Policy.",
          "For users in the European Economic Area (EEA), United Kingdom, or Switzerland, we have appointed a Data Protection Officer (DPO) who can be contacted at dpo@unimerg.com.",
        ],
      },
      {
        title: "1.2 Scope of This Policy",
        content: [
          "This Policy covers all personal information collected through: our website (unimerg.com and related domains), our mobile and desktop applications, our contributor platform, email and other communications, customer support interactions, and any other services we provide.",
          "This Policy does not apply to third-party websites, applications, or services that may be linked from our Services. We encourage you to review the privacy policies of any third-party services you access.",
        ],
      },
    ],
  },
  {
    id: "information-collected",
    title: "2. Information We Collect",
    content: [
      "We collect various types of personal information to provide and improve our Services, comply with legal obligations, and protect our platform and users. The information we collect depends on how you interact with us.",
    ],
    subsections: [
      {
        title: "2.1 Identity Information",
        content: [
          "We collect identity information to verify who you are and prevent fraud. This includes:",
          "Full legal name as it appears on official identification documents, including first name, middle name(s), last name, and any suffixes or titles.",
          "Date of birth to verify age eligibility and for identity verification purposes.",
          "Government-issued identification numbers, such as national ID number, passport number, driver's license number, or social security number (where legally required).",
          "Photographs and images from your identification documents and selfie/video verification.",
          "Biometric data, including facial geometry and voice patterns collected during verification processes.",
          "Gender and nationality as indicated on identification documents.",
        ],
      },
      {
        title: "2.2 Contact Information",
        content: [
          "We collect contact information to communicate with you and provide our Services:",
          "Email address (primary and secondary), phone number (mobile and/or landline), residential address (street address, city, state/province, postal code, country), and emergency contact information (if voluntarily provided).",
        ],
      },
      {
        title: "2.3 Verification Documents",
        content: [
          "To verify your identity and comply with legal requirements, we collect copies of:",
          "Government-issued photo identification: passport, national ID card, driver's license, residence permit, or other official photo ID.",
          "Proof of address documents: utility bills (electricity, gas, water, internet), bank or credit card statements, government correspondence, tax documents, lease or rental agreements, or mortgage statements (documents must be dated within 3 months).",
          "Professional credentials: educational diplomas and certificates, professional licenses, industry certifications, employment verification letters, and academic transcripts.",
          "Financial documents: tax identification documents (W-9, W-8BEN, or local equivalents), bank account verification documents, and payment processor account verification.",
        ],
      },
      {
        title: "2.4 Financial Information",
        content: [
          "To process payments and comply with tax regulations, we collect:",
          "Bank account details: account holder name, bank name, account number, routing number, IBAN, SWIFT/BIC code.",
          "Payment processor account information: PayPal email, Wise account details, Payoneer account information.",
          "Tax information: tax identification number (SSN, TIN, VAT number, or equivalent), tax residency status, tax forms and declarations.",
          "Transaction history: payment amounts, dates, and methods.",
        ],
      },
      {
        title: "2.5 Professional Information",
        content: [
          "To match you with appropriate projects and verify qualifications:",
          "Educational background: degrees, institutions, graduation dates, fields of study.",
          "Work experience: employment history, job titles, employers, dates of employment.",
          "Skills and expertise: languages spoken, technical skills, domain expertise, certifications.",
          "Professional references: names, contact information, and relationships (if provided).",
        ],
      },
      {
        title: "2.6 Platform Activity Information",
        content: [
          "We collect information about how you use our Services:",
          "Account activity: login times, session duration, features accessed, actions taken.",
          "Task and project data: tasks viewed, accepted, and completed; time spent on tasks; quality scores and feedback.",
          "Communication data: messages sent through the platform, support tickets, feedback submitted.",
          "Preferences and settings: notification preferences, language settings, accessibility options.",
        ],
      },
      {
        title: "2.7 Device and Technical Information",
        content: [
          "We automatically collect technical information when you use our Services:",
          "Device information: device type, model, operating system, browser type and version, screen resolution.",
          "Network information: IP address, internet service provider, connection type.",
          "Identifiers: device identifiers, advertising identifiers, cookies, and similar technologies.",
          "Location data: approximate location based on IP address; precise location only with your explicit consent.",
        ],
      },
      {
        title: "2.8 Biometric Information",
        content: [
          "For identity verification and security purposes, we may collect:",
          "Facial recognition data: facial geometry and features extracted from photos or videos for identity matching.",
          "Voice data: voice recordings and voiceprints for voice verification (where applicable).",
          "Liveness detection data: data confirming you are a real person during verification.",
          "Biometric data is collected only with your explicit consent where required by law. You may have rights regarding biometric data under applicable state or national laws.",
        ],
      },
      {
        title: "2.9 Sensitive Personal Information",
        content: [
          "In certain circumstances, we may collect sensitive personal information, including:",
          "Government identifiers: social security numbers, national ID numbers, passport numbers.",
          "Financial account information: bank account and payment card details.",
          "Biometric information: as described above.",
          "Precise geolocation: only with explicit consent.",
          "We process sensitive personal information only as necessary for the purposes described in this Policy and in compliance with applicable law.",
        ],
      },
    ],
  },
  {
    id: "how-we-collect",
    title: "3. How We Collect Information",
    content: [
      "We collect personal information through various methods, depending on how you interact with our Services.",
    ],
    subsections: [
      {
        title: "3.1 Information You Provide Directly",
        content: [
          "We collect information that you voluntarily provide when you:",
          "Create an account or register on our platform.",
          "Complete your profile or update account information.",
          "Submit verification documents for identity verification.",
          "Apply for or accept projects and tasks.",
          "Communicate with us through support channels, email, or chat.",
          "Participate in surveys, promotions, or research studies.",
          "Submit feedback, reviews, or testimonials.",
          "Apply for employment or contractor positions.",
        ],
      },
      {
        title: "3.2 Information Collected Automatically",
        content: [
          "We automatically collect certain information when you use our Services through:",
          "Cookies and similar tracking technologies (see Section 11).",
          "Server logs that record requests made to our servers.",
          "Analytics tools that track usage patterns and performance.",
          "Security monitoring systems that detect threats and anomalies.",
        ],
      },
      {
        title: "3.3 Information from Third Parties",
        content: [
          "We may receive information about you from third-party sources, including:",
          "Identity verification services: verification results, fraud risk scores, document authentication results.",
          "Background check providers: where permitted by law and with your consent.",
          "Payment processors: transaction confirmations, account verification status.",
          "Social media platforms: if you choose to link your accounts or sign in using social login.",
          "Public databases: publicly available information for verification purposes.",
          "Referral sources: information provided by users who refer you to our platform.",
          "Business partners and clients: project-related information.",
        ],
      },
      {
        title: "3.4 Information from Your Device",
        content: [
          "With your permission, we may access information from your device:",
          "Camera: for document scanning and facial verification.",
          "Microphone: for voice verification (where applicable).",
          "Photo library: for uploading verification documents.",
          "Location services: for location verification (with explicit consent).",
          "You can manage these permissions through your device settings.",
        ],
      },
    ],
  },
  {
    id: "use-of-information",
    title: "4. How We Use Your Information",
    content: [
      "We use your personal information for various purposes related to providing, improving, and securing our Services.",
    ],
    subsections: [
      {
        title: "4.1 Providing and Managing Services",
        content: [
          "We use your information to:",
          "Create and manage your account.",
          "Verify your identity and prevent fraud.",
          "Match you with appropriate projects and tasks.",
          "Process and facilitate your work on the platform.",
          "Communicate with you about your account, projects, and tasks.",
          "Provide customer support and respond to inquiries.",
          "Process payments and manage financial transactions.",
        ],
      },
      {
        title: "4.2 Identity Verification and Fraud Prevention",
        content: [
          "We use your information to:",
          "Verify that you are who you claim to be.",
          "Authenticate submitted documents.",
          "Detect and prevent fraudulent activity.",
          "Comply with Know Your Customer (KYC) and Anti-Money Laundering (AML) requirements.",
          "Maintain platform integrity and security.",
          "Investigate suspicious activities and potential violations.",
        ],
      },
      {
        title: "4.3 Legal and Regulatory Compliance",
        content: [
          "We use your information to:",
          "Comply with applicable laws, regulations, and legal processes.",
          "Fulfill tax reporting and withholding obligations.",
          "Respond to lawful requests from government authorities.",
          "Enforce our Terms of Service and other agreements.",
          "Protect our legal rights and interests.",
          "Cooperate with law enforcement when required.",
        ],
      },
      {
        title: "4.4 Communication",
        content: [
          "We use your information to:",
          "Send transactional communications (account updates, payment confirmations, task notifications).",
          "Provide important service announcements and updates.",
          "Send marketing communications (with your consent where required).",
          "Conduct surveys and request feedback.",
          "Respond to your inquiries and support requests.",
        ],
      },
      {
        title: "4.5 Improvement and Development",
        content: [
          "We use your information to:",
          "Analyze usage patterns and trends.",
          "Improve our Services, features, and user experience.",
          "Develop new products and services.",
          "Conduct research and analytics.",
          "Test and implement new features.",
          "Train and improve our systems, including AI/ML models (in accordance with project agreements).",
        ],
      },
      {
        title: "4.6 Security and Protection",
        content: [
          "We use your information to:",
          "Protect the security and integrity of our platform.",
          "Detect, prevent, and respond to security threats.",
          "Monitor for unauthorized access or suspicious activity.",
          "Investigate and address potential violations.",
          "Maintain audit logs for security purposes.",
        ],
      },
      {
        title: "4.7 Business Operations",
        content: [
          "We use your information to:",
          "Manage our business operations and administration.",
          "Process transactions and maintain financial records.",
          "Comply with corporate governance requirements.",
          "Conduct internal audits and assessments.",
          "Manage disputes and legal claims.",
        ],
      },
    ],
  },
  {
    id: "legal-basis",
    title: "5. Legal Basis for Processing",
    content: [
      "We process your personal information based on various legal grounds, depending on the purpose and context of the processing.",
    ],
    subsections: [
      {
        title: "5.1 Contractual Necessity",
        content: [
          "We process personal information as necessary to perform our contract with you, including:",
          "Creating and managing your account.",
          "Providing access to our platform and services.",
          "Processing payments for completed work.",
          "Communicating about your account and tasks.",
        ],
      },
      {
        title: "5.2 Legal Obligations",
        content: [
          "We process personal information to comply with legal obligations, including:",
          "Tax reporting and withholding requirements.",
          "KYC/AML compliance obligations.",
          "Responding to valid legal requests.",
          "Maintaining required records.",
        ],
      },
      {
        title: "5.3 Legitimate Interests",
        content: [
          "We process personal information based on our legitimate interests, where those interests are not overridden by your rights, including:",
          "Fraud prevention and security.",
          "Improving and developing our Services.",
          "Marketing our Services (where consent is not required).",
          "Enforcing our terms and protecting our rights.",
          "Business administration and operations.",
        ],
      },
      {
        title: "5.4 Consent",
        content: [
          "We process certain personal information based on your consent, including:",
          "Biometric data for identity verification.",
          "Precise location data.",
          "Marketing communications (where consent is required).",
          "Sensitive personal information (where required by law).",
          "You may withdraw consent at any time, though this may affect your ability to use certain features.",
        ],
      },
      {
        title: "5.5 Vital Interests",
        content: [
          "In rare circumstances, we may process personal information to protect vital interests of you or another person, such as in emergencies.",
        ],
      },
    ],
  },
  {
    id: "sharing-information",
    title: "6. Sharing Your Information",
    content: [
      "We do not sell your personal information. We may share your information with third parties only as described in this Policy.",
    ],
    subsections: [
      {
        title: "6.1 Service Providers",
        content: [
          "We share information with third-party service providers who perform services on our behalf, including:",
          "Identity verification providers: to verify your identity and authenticate documents.",
          "Payment processors: to process payments and transactions.",
          "Cloud hosting providers: to store and process data.",
          "Analytics providers: to analyze usage and improve our Services.",
          "Communication providers: to send emails, SMS, and notifications.",
          "Customer support platforms: to manage support inquiries.",
          "Security providers: to protect against fraud and security threats.",
          "Service providers are bound by contractual obligations to protect your information and use it only for the purposes we specify.",
        ],
      },
      {
        title: "6.2 Business Partners and Clients",
        content: [
          "We may share limited information with our business partners and clients:",
          "Project-related information necessary for task completion.",
          "Quality metrics and performance data (typically in anonymized or aggregated form).",
          "Information required for compliance with client agreements.",
          "We require partners and clients to protect your information and use it only for authorized purposes.",
        ],
      },
      {
        title: "6.3 Legal and Regulatory Disclosures",
        content: [
          "We may disclose information when required by law or legal process:",
          "In response to valid subpoenas, court orders, or government requests.",
          "To comply with applicable laws, regulations, or legal obligations.",
          "To cooperate with law enforcement or regulatory authorities.",
          "To protect our rights, property, or safety, or that of others.",
          "To enforce our terms, agreements, and policies.",
        ],
      },
      {
        title: "6.4 Business Transfers",
        content: [
          "In connection with business transactions, we may share or transfer information:",
          "During negotiations or due diligence for mergers, acquisitions, or sales.",
          "As part of a merger, acquisition, bankruptcy, or reorganization.",
          "To successors or assigns in connection with a business transfer.",
          "We will notify you of any change in ownership or control that affects your personal information.",
        ],
      },
      {
        title: "6.5 Affiliated Companies",
        content: [
          "We may share information with our parent company, subsidiaries, and affiliates for purposes consistent with this Policy, including shared services, administration, and business operations.",
        ],
      },
      {
        title: "6.6 With Your Consent",
        content: [
          "We may share information with other third parties when you have given us your explicit consent to do so.",
        ],
      },
      {
        title: "6.7 Aggregated and De-identified Information",
        content: [
          "We may share aggregated, anonymized, or de-identified information that cannot reasonably be used to identify you. This information may be used for analytics, research, industry reports, and other purposes.",
        ],
      },
    ],
  },
  {
    id: "data-retention",
    title: "7. Data Retention",
    content: [
      "We retain personal information for as long as necessary to fulfill the purposes for which it was collected and to comply with legal obligations.",
    ],
    subsections: [
      {
        title: "7.1 Retention Periods",
        content: [
          "Our retention periods vary based on the type of information and applicable legal requirements:",
          "Account information: Retained for the duration of your account and for 5 years after account closure.",
          "Identity verification documents: Retained for a minimum of 5 years after account closure to comply with KYC/AML requirements; may be retained longer based on jurisdictional requirements.",
          "Financial and tax records: Retained for 7 years or longer as required by tax authorities.",
          "Transaction records: Retained for 7 years for tax and audit purposes.",
          "Communication records: Retained for 3 years for customer support quality and dispute resolution.",
          "Platform activity logs: Retained for 2 years for security and analytics purposes.",
          "Marketing preferences: Retained until you withdraw consent or unsubscribe.",
        ],
      },
      {
        title: "7.2 Criteria for Retention",
        content: [
          "We determine retention periods based on:",
          "The purpose for which the information was collected.",
          "Legal, regulatory, and contractual obligations.",
          "Statute of limitations for potential legal claims.",
          "Business operational needs.",
          "Your requests regarding your data.",
        ],
      },
      {
        title: "7.3 Deletion and Anonymization",
        content: [
          "When retention periods expire, we will:",
          "Securely delete the information, or",
          "Anonymize the information so it can no longer identify you.",
          "Some information may be retained in backup systems for a limited period before being permanently deleted.",
        ],
      },
      {
        title: "7.4 Retention After Account Deletion",
        content: [
          "If you request account deletion, we will delete or anonymize your personal information, subject to:",
          "Legal retention requirements (e.g., tax, KYC/AML).",
          "Ongoing disputes or legal claims.",
          "Fraud prevention and security needs.",
          "We will inform you if certain information must be retained and for how long.",
        ],
      },
    ],
  },
  {
    id: "data-security",
    title: "8. Data Security",
    content: [
      "We implement comprehensive security measures to protect your personal information from unauthorized access, disclosure, alteration, and destruction.",
    ],
    subsections: [
      {
        title: "8.1 Technical Safeguards",
        content: [
          "We employ various technical security measures, including:",
          "Encryption: All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption.",
          "Access controls: Role-based access controls, multi-factor authentication, and the principle of least privilege.",
          "Network security: Firewalls, intrusion detection systems, and DDoS protection.",
          "Secure development: Security-focused software development practices, code reviews, and vulnerability testing.",
          "Monitoring: Continuous security monitoring, logging, and alerting systems.",
        ],
      },
      {
        title: "8.2 Organizational Safeguards",
        content: [
          "We maintain organizational security measures, including:",
          "Security policies: Comprehensive information security policies and procedures.",
          "Employee training: Regular security awareness training for all employees.",
          "Access management: Strict controls on who can access personal information.",
          "Vendor management: Security assessments and contractual requirements for third-party vendors.",
          "Incident response: Documented incident response procedures and teams.",
        ],
      },
      {
        title: "8.3 Physical Safeguards",
        content: [
          "Our physical security measures include:",
          "Secure data centers with 24/7 security, access controls, and environmental protections.",
          "Secure office facilities with controlled access.",
          "Secure disposal of physical media containing personal information.",
        ],
      },
      {
        title: "8.4 Certifications and Compliance",
        content: [
          "We maintain various security certifications and compliance frameworks:",
          "SOC 2 Type II certification.",
          "ISO 27001 certification.",
          "Regular third-party security audits and penetration testing.",
          "Compliance with PCI DSS for payment card data (where applicable).",
        ],
      },
      {
        title: "8.5 Your Security Responsibilities",
        content: [
          "You also play an important role in protecting your information:",
          "Use strong, unique passwords and enable two-factor authentication.",
          "Keep your login credentials confidential.",
          "Log out when using shared devices.",
          "Report any suspicious activity or security concerns to us immediately.",
          "Keep your devices and software updated.",
        ],
      },
      {
        title: "8.6 Security Limitations",
        content: [
          "While we implement robust security measures, no system is completely secure. We cannot guarantee absolute security of your information. In the event of a security breach, we will notify you and relevant authorities as required by applicable law.",
        ],
      },
    ],
  },
  {
    id: "international-transfers",
    title: "9. International Data Transfers",
    content: [
      "As a global platform, we may transfer your personal information to countries other than your country of residence.",
    ],
    subsections: [
      {
        title: "9.1 Transfer Locations",
        content: [
          "Your personal information may be transferred to and processed in:",
          "The United States, where our primary operations are located.",
          "Other countries where our service providers operate.",
          "Locations necessary for providing our Services globally.",
          "These countries may have different data protection laws than your country of residence.",
        ],
      },
      {
        title: "9.2 Transfer Safeguards",
        content: [
          "When transferring personal information internationally, we implement appropriate safeguards:",
          "Standard Contractual Clauses (SCCs) approved by the European Commission.",
          "Data Processing Agreements with all processors.",
          "Binding Corporate Rules for intra-group transfers (where applicable).",
          "Adequacy decisions where the destination country provides adequate protection.",
          "Your explicit consent where other safeguards are not available.",
        ],
      },
      {
        title: "9.3 EU-US Data Privacy Framework",
        content: [
          "For transfers from the European Economic Area, United Kingdom, or Switzerland to the United States, we rely on the EU-US Data Privacy Framework and related mechanisms where applicable.",
        ],
      },
      {
        title: "9.4 Your Rights Regarding Transfers",
        content: [
          "You may request information about the safeguards we use for international transfers. Contact us using the information provided at the end of this Policy.",
        ],
      },
    ],
  },
  {
    id: "your-rights",
    title: "10. Your Privacy Rights",
    content: [
      "Depending on your location and applicable law, you may have various rights regarding your personal information.",
    ],
    subsections: [
      {
        title: "10.1 Right to Access",
        content: [
          "You have the right to request access to your personal information, including:",
          "Confirmation of whether we process your personal information.",
          "A copy of the personal information we hold about you.",
          "Information about how we use your personal information.",
        ],
      },
      {
        title: "10.2 Right to Rectification",
        content: [
          "You have the right to request correction of inaccurate or incomplete personal information. You can update much of your information directly through your account settings.",
        ],
      },
      {
        title: "10.3 Right to Erasure (Right to be Forgotten)",
        content: [
          "You have the right to request deletion of your personal information in certain circumstances, such as:",
          "When the information is no longer necessary for the purposes collected.",
          "When you withdraw consent (where processing is based on consent).",
          "When you object to processing and there are no overriding legitimate grounds.",
          "When the information was unlawfully processed.",
          "This right is not absolute and may be limited by legal retention requirements.",
        ],
      },
      {
        title: "10.4 Right to Restriction",
        content: [
          "You have the right to request restriction of processing in certain circumstances, such as:",
          "While we verify the accuracy of your information.",
          "When processing is unlawful but you prefer restriction over deletion.",
          "When we no longer need the information but you need it for legal claims.",
          "While we consider your objection to processing.",
        ],
      },
      {
        title: "10.5 Right to Data Portability",
        content: [
          "You have the right to receive your personal information in a structured, commonly used, machine-readable format and to transmit it to another controller, where technically feasible and where processing is based on consent or contract.",
        ],
      },
      {
        title: "10.6 Right to Object",
        content: [
          "You have the right to object to processing based on legitimate interests or for direct marketing purposes. We will stop processing unless we demonstrate compelling legitimate grounds or the processing is for legal claims.",
        ],
      },
      {
        title: "10.7 Rights Related to Automated Decision-Making",
        content: [
          "You have the right not to be subject to decisions based solely on automated processing that produce legal or similarly significant effects, except where permitted by law. Where such decisions are made, you have the right to meaningful information about the logic involved and to human intervention.",
        ],
      },
      {
        title: "10.8 Right to Withdraw Consent",
        content: [
          "Where processing is based on consent, you have the right to withdraw consent at any time. Withdrawal does not affect the lawfulness of processing before withdrawal.",
        ],
      },
      {
        title: "10.9 Right to Complain",
        content: [
          "You have the right to lodge a complaint with a supervisory authority if you believe your privacy rights have been violated. We encourage you to contact us first so we can address your concerns.",
        ],
      },
      {
        title: "10.10 Exercising Your Rights",
        content: [
          "To exercise your rights, you may:",
          "Use the privacy settings in your account.",
          "Contact us at privacy@unimerg.com.",
          "Submit a request through our Help Center.",
          "We will respond to requests within the timeframe required by applicable law (typically 30 days). We may need to verify your identity before processing your request.",
        ],
      },
      {
        title: "10.11 Non-Discrimination",
        content: [
          "We will not discriminate against you for exercising your privacy rights. However, some rights may affect your ability to use certain features of our Services.",
        ],
      },
    ],
  },
  {
    id: "cookies-tracking",
    title: "11. Cookies & Tracking Technologies",
    content: [
      "We use cookies and similar tracking technologies to collect information, improve our Services, and provide personalized experiences.",
    ],
    subsections: [
      {
        title: "11.1 Types of Cookies We Use",
        content: [
          "Essential Cookies: Required for the operation of our Services. They enable core functionality such as security, account access, and remembering your preferences. These cookies cannot be disabled.",
          "Performance Cookies: Collect information about how you use our Services, such as pages visited and errors encountered. This helps us improve our Services.",
          "Functional Cookies: Enable enhanced functionality and personalization, such as remembering your preferences and settings.",
          "Analytics Cookies: Help us understand how visitors interact with our Services by collecting and reporting information anonymously.",
          "Marketing Cookies: Used to track visitors across websites to display relevant advertisements (used only with consent where required).",
        ],
      },
      {
        title: "11.2 Other Tracking Technologies",
        content: [
          "In addition to cookies, we may use:",
          "Web beacons (pixel tags): Small graphics that track whether you have opened an email or visited a page.",
          "Local storage: Browser storage that persists data locally on your device.",
          "Session storage: Temporary browser storage cleared when you close your browser.",
          "Device fingerprinting: Collecting device characteristics for security and fraud prevention.",
        ],
      },
      {
        title: "11.3 Third-Party Cookies",
        content: [
          "Some cookies are placed by third parties on our behalf, including:",
          "Analytics providers (e.g., Google Analytics).",
          "Advertising networks (where applicable).",
          "Social media platforms (for social features).",
          "Security and fraud prevention services.",
          "Third parties have their own privacy policies governing their use of cookies.",
        ],
      },
      {
        title: "11.4 Managing Cookies",
        content: [
          "You can manage cookies through:",
          "Our cookie consent banner when you first visit our site.",
          "Cookie preference settings in your account.",
          "Your browser settings (note that blocking cookies may affect functionality).",
          "Opt-out tools provided by advertising networks.",
          "For more information, see our Cookie Policy.",
        ],
      },
      {
        title: "11.5 Do Not Track",
        content: [
          'Some browsers have a "Do Not Track" feature. Our Services do not currently respond to "Do Not Track" signals. However, you can manage your privacy preferences through the methods described above.',
        ],
      },
    ],
  },
  {
    id: "children-privacy",
    title: "12. Children's Privacy",
    content: [
      "Our Services are not directed to children, and we do not knowingly collect personal information from children.",
    ],
    subsections: [
      {
        title: "12.1 Age Requirements",
        content: [
          "You must be at least 18 years old (or the age of majority in your jurisdiction, if higher) to use our Services. We do not knowingly collect personal information from anyone under 18 years of age.",
        ],
      },
      {
        title: "12.2 Parental Notice",
        content: [
          "If we learn that we have collected personal information from a child under 18, we will take steps to delete that information as quickly as possible. If you believe we have collected information from a child, please contact us immediately at privacy@unimerg.com.",
        ],
      },
      {
        title: "12.3 COPPA Compliance",
        content: [
          "We comply with the Children's Online Privacy Protection Act (COPPA) in the United States. Our Services are not directed to children under 13, and we do not knowingly collect personal information from children under 13.",
        ],
      },
    ],
  },
  {
    id: "third-party-links",
    title: "13. Third-Party Links & Services",
    content: [
      "Our Services may contain links to third-party websites, applications, or services that are not operated by us.",
    ],
    subsections: [
      {
        title: "13.1 Third-Party Websites",
        content: [
          "We are not responsible for the privacy practices of third-party websites. When you leave our Services and visit a third-party site, you are subject to that site's privacy policy. We encourage you to review the privacy policies of any third-party sites you visit.",
        ],
      },
      {
        title: "13.2 Third-Party Integrations",
        content: [
          "Some of our Services may integrate with third-party services (e.g., payment processors, social media platforms). When you use these integrations, you may be sharing information with the third party according to their privacy policy.",
        ],
      },
      {
        title: "13.3 Social Media Features",
        content: [
          "Our Services may include social media features (e.g., share buttons, social login). These features may collect information such as your IP address and pages visited and may set cookies. Your interactions with these features are governed by the privacy policy of the social media company.",
        ],
      },
    ],
  },
  {
    id: "policy-changes",
    title: "14. Changes to This Policy",
    content: [
      "We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors.",
    ],
    subsections: [
      {
        title: "14.1 Notification of Changes",
        content: [
          "When we make material changes to this Policy, we will notify you by:",
          'Posting the updated Policy on our website with a new "Last Updated" date.',
          "Sending an email to the address associated with your account.",
          "Displaying a prominent notice on our platform.",
          "Requesting your consent to material changes where required by law.",
        ],
      },
      {
        title: "14.2 Review of Changes",
        content: [
          "We encourage you to review this Policy periodically to stay informed about our privacy practices. Your continued use of our Services after any changes indicates your acceptance of the updated Policy.",
        ],
      },
      {
        title: "14.3 Previous Versions",
        content: [
          "Previous versions of this Policy are available upon request. Contact us at privacy@unimerg.com for archived versions.",
        ],
      },
    ],
  },
  {
    id: "contact-us",
    title: "15. Contact Us",
    content: [
      "If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us.",
    ],
    subsections: [
      {
        title: "15.1 Privacy Inquiries",
        content: [
          "For general privacy questions or to exercise your privacy rights:",
          "Email: privacy@unimerg.com",
          "Online: Submit a request through our Help Center at unimerg.com/help",
        ],
      },
      {
        title: "15.2 Data Protection Officer",
        content: [
          "For users in the EEA, UK, or Switzerland, you may contact our Data Protection Officer:",
          "Email: dpo@unimerg.com",
          "Address: Unimerg Inc., Attn: Data Protection Officer, 123 Innovation Drive, Suite 500, San Francisco, CA 94105, USA",
        ],
      },
      {
        title: "15.3 Mailing Address",
        content: [
          "Unimerg Inc.",
          "Attn: Privacy Team",
          "123 Innovation Drive, Suite 500",
          "San Francisco, CA 94105",
          "United States",
        ],
      },
      {
        title: "15.4 Response Time",
        content: [
          "We will acknowledge receipt of your inquiry within 48 hours and respond substantively within 30 days, or as required by applicable law. Complex requests may require additional time, and we will keep you informed of our progress.",
        ],
      },
    ],
  },
  {
    id: "jurisdiction-specific",
    title: "16. Jurisdiction-Specific Disclosures",
    content: [
      "Depending on your location, additional rights and disclosures may apply to you.",
    ],
    subsections: [
      {
        title: "16.1 European Economic Area, United Kingdom, and Switzerland",
        content: [
          "If you are located in the EEA, UK, or Switzerland, you have rights under the General Data Protection Regulation (GDPR) or equivalent laws, including:",
          "All rights described in Section 10 of this Policy.",
          "The right to lodge a complaint with your local supervisory authority.",
          "The right to receive information about international transfers and safeguards.",
          "Our legal bases for processing are described in Section 5.",
          "For UK residents, our representative can be contacted at uk-representative@unimerg.com.",
        ],
      },
      {
        title: "16.2 California Residents (CCPA/CPRA)",
        content: [
          "If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):",
          "Right to Know: You can request information about the categories and specific pieces of personal information we have collected, the sources, purposes, and third parties with whom we share it.",
          "Right to Delete: You can request deletion of your personal information, subject to exceptions.",
          "Right to Correct: You can request correction of inaccurate personal information.",
          "Right to Opt-Out of Sale/Sharing: We do not sell your personal information. You can opt out of sharing for cross-context behavioral advertising.",
          "Right to Limit Use of Sensitive Personal Information: You can limit our use of sensitive personal information to purposes necessary for providing our Services.",
          "Right to Non-Discrimination: You will not be discriminated against for exercising your rights.",
          "To exercise these rights, contact us at privacy@unimerg.com or call 1-800-XXX-XXXX.",
          "Authorized agents may submit requests on your behalf with proper authorization.",
        ],
      },
      {
        title: "16.3 Virginia, Colorado, Connecticut, and Other US States",
        content: [
          "If you are a resident of Virginia, Colorado, Connecticut, or other states with comprehensive privacy laws, you may have similar rights to those described for California residents, including rights to access, delete, correct, and opt out of certain processing. Contact us to exercise these rights.",
        ],
      },
      {
        title: "16.4 Brazil (LGPD)",
        content: [
          "If you are located in Brazil, you have rights under the Lei Geral de Proteção de Dados (LGPD), including:",
          "Confirmation of processing and access to your data.",
          "Correction of incomplete, inaccurate, or outdated data.",
          "Anonymization, blocking, or deletion of unnecessary or excessive data.",
          "Data portability.",
          "Information about sharing with third parties.",
          "Revocation of consent.",
          "Our DPO for Brazil can be contacted at brazil-dpo@unimerg.com.",
        ],
      },
      {
        title: "16.5 Canada (PIPEDA)",
        content: [
          "If you are located in Canada, your personal information is protected under the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial laws. You have rights to access and correct your personal information and to withdraw consent to certain processing.",
        ],
      },
      {
        title: "16.6 Australia",
        content: [
          "If you are located in Australia, your personal information is protected under the Privacy Act 1988 and the Australian Privacy Principles. You have rights to access and correct your personal information. If you believe we have breached the Privacy Act, you may lodge a complaint with the Office of the Australian Information Commissioner (OAIC).",
        ],
      },
      {
        title: "16.7 Other Jurisdictions",
        content: [
          "If you are located in a jurisdiction not specifically mentioned above, you may still have rights under applicable local laws. Contact us at privacy@unimerg.com for information about your specific rights.",
        ],
      },
    ],
  },
];

export const privacySummary = {
  title: "Privacy at a Glance",
  items: [
    {
      icon: "Shield",
      title: "What We Collect",
      description:
        "Identity documents, contact info, financial details, professional credentials, and usage data to provide our services.",
    },
    {
      icon: "Lock",
      title: "How We Protect It",
      description:
        "AES-256 encryption, SOC 2 certified, regular security audits, and strict access controls.",
    },
    {
      icon: "Users",
      title: "Who We Share With",
      description:
        "Only service providers, partners (as needed), and authorities (when legally required).",
    },
    {
      icon: "Clock",
      title: "How Long We Keep It",
      description:
        "As long as necessary for services and legal compliance—typically 5-7 years for verification and financial records.",
    },
    {
      icon: "UserCheck",
      title: "Your Rights",
      description:
        "Access, correct, delete, port, and object to processing of your data. Exercise rights via privacy@unimerg.com.",
    },
    {
      icon: "Globe",
      title: "International Transfers",
      description:
        "Data may be transferred globally with appropriate safeguards like Standard Contractual Clauses.",
    },
  ],
};
